import React, { JSX, useContext, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { toast } from "react-toastify";
import { ExportService } from "@app/exports";
import { CountyProjectContext } from "@app/projects";
import { ProviderNetworkType, RegionSetStatusType } from "@app/projects/enumerators";
import {
  PciDashboardDetailsModel,
  PciDashboardFiltersContext,
  PciDashboardFiltersOverview,
  PciDashboardFiltersPayload,
  PciDashboardService,
} from "@app/projects/network-assessment/pci-dashboard/shared";
import ProviderTerminationService from "@app/provider-terminations/providerTerminationService";
import { PageHeader, PageHeaderRight, TooltipWrapper, IconToggle } from "@app/shared";
import { RoleRestricted, Roles, UserContext } from "@app/user"
import { SortDirection } from "@app/shared/griddle/enumerators";
import { QESAlert, QESButton } from "@app/shared/qes-standards";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ModalLink } from "@app/shared/Modals";
import { ColumnConfig, SortDirections, TextAlignment, Waffles } from "@app/shared/waffles";
import { Features } from "@app/feature-flags";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { TerminatePanelMode, WrappedTerminateProvidersPanel as TerminateProvidersPanel } from "@app/projects/servicing-provider/TerminateProvidersPanel";
import { ClaimsActivityVolumeLabelEnum, PciDashboardProviderTerminationStatusEnum, PciDashboardProviderTerminationTypeEnum, PciDashboardTerminationDetails } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import PciDashboardProviderFlyout from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardProviderFlyout";

interface PciDashboardDetailsViewProps {
  setShowDashboardDetails: (showDashboardDetails: boolean) => void;
  showLineOfBusiness: boolean;
}

interface PciDashboardDetailsViewWaffleToppings {
  commercialClaimsLabel: string;
  effectivenessRank: string;
  efficiencyRank: string;
  key: string;
  medicaidClaimsLabel: string;
  medicareClaimsLabel: string;  
  providerName: JSX.Element;
  totalClaimsLabel: string;
  tricareClaimsLabel: string;
  vaClaimsLabel: string;
  terminationReason?: JSX.Element;
  commercialNewPatientInd: string;
  medicaidNewPatientInd: string;
  medicareNewPatientInd: string;
  totalNewPatientInd: string;
  vaNewPatientInd: string;
  tricareNewPatientInd: string;
}

const DashboardDetailsView = (props: PciDashboardDetailsViewProps): JSX.Element => {
  const [createReportLoading, setCreateReportLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [dashboardDetailsViewToppings, setDashboardDetailsViewToppings] = useState<PciDashboardDetailsViewWaffleToppings[]>([]);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [sortedColumn, setSortedColumn] = useState<string>(null);
  const [totalCount, setTotalCount] = useState<number>(null);
  const [totalNotTerminatedCount, setTotalNotTerminatedCount] = useState<number>(null);
  const [showTerminateButton, setShowTerminateButton] = useState<boolean>(false);
  const [showTerminatedProviders, setShowTerminatedProviders] = useState<boolean>(false);
  const [npis, setNpis] = useState<string[]>(null);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [terminatedNpisExist, setTerminatedNpisExist] = useState<boolean>(false);
  const [npiAndTerminationId, setNpiAndTerminationId] = useState<[string, number][]>([]);
  const [restoringAll, setRestoringAll] = useState<boolean>(false);
  const [isNetworkGhostFilters, setIsNetworkGhostFilters] = useState<boolean>(false);
  const [error, setError] = useState("");
  
  const exportService = new ExportService();
  const pciDashboardFiltersContext = useContext(PciDashboardFiltersContext);
  const pciDashboardService = new PciDashboardService();
  const providerTerminationService = new ProviderTerminationService();
  const projectContext = useContext(CountyProjectContext);
  const featureFlags = React.useContext(FeatureFlagContext).enabledFeatureFlags;
  const user = React.useContext(UserContext);

  const newPatientClaimsToolTip = "A New Patient Claim is defined as a new patient office visit which is represented by codes 99202 - 99205 on the previous 15 months of Patient Claims"

  function getEffectivenessDefinition(): JSX.Element {
    return (
      <div className="text-left">
        Effectiveness is a measure of quality where 5 is the best score (higher quality) and 1 is the worst score (lower quality). Scores are relative to
        other providers in the same specialty and state and are derived from claims insight into 1) Mortality 2) Complications 3) Days in acute care.
      </div>
    );
  }

  function getEfficencyDefinition(): JSX.Element {
    return (
      <div className="text-left">
        Efficiency is a measure of physician cost vs peers where 5 is the best score (lower cost) and 1 is the worst
        score (higher cost). Efficiency measures are based on 1) cost per visit 2) cost per patient. Standardized pricing
        methodology is used, meaning efficiency is a measure of comparative utilization intensity and not rate/price differences.
      </div>
    );
  }

  function getClaimsVolumeDefinitions(): JSX.Element {
    return (
      <div className="text-left">
        Ghost: 0 patients<br />
        Peripheral: &lt;24th percentile in patient volume (based on population density and specialty).<br />
        Standard: 25th-74th percentile in patient volume (based on population density and specialty).<br />
        Core: 75th+ percentile in patient volume (based on population density and specialty).<br />
        Insufficient Data - New: There is no claims activity for this provider but because the NPI was recently issued they have not been labeled as a ghost.<br />
        Insufficient Data - State/LOB: There is no claims activity for this provider but because of limited claims coverage in this State/LOB they have not been labeled as a ghost.< br />
        Insufficient Data - Specialty: There is no claims activity for this provider but because of limited claims volume for this specialty they have not been labeled as a ghost.<br />
        RX Only: Claims in which only prescriptions are present.<br />
      </div>
    );
  }

  useEffect(() => {
    if (!!projectContext.project && !!projectContext.runConfiguration) {
      getDashboardDetails(showTerminatedProviders, null, null, null, true);
    }
  }, [projectContext.project, projectContext.runConfiguration, projectContext]);

  const columnConfig: ColumnConfig = {
    commercialClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Commercial Claims Volume",
      testId: "commercial-claims-volume",
      tooltip: getClaimsVolumeDefinitions()
    },
    ...(featureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT) && {
      commercialNewPatientInd: {
        alignment: TextAlignment.Left,
        displayText: "Commercial New Patient Claims",
        testId: "commercial-new-patient-claims",
        tooltip: newPatientClaimsToolTip
      },
    }),
    effectivenessRank: {
      alignment: TextAlignment.Left,
      displayText: "Effectiveness",
      testId: "effectiveness",
      tooltip: getEffectivenessDefinition()
    },
    efficiencyRank: {
      alignment: TextAlignment.Left,
      displayText: "Efficiency",
      testId: "efficiency",
      tooltip: getEfficencyDefinition()
    },
    medicaidClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Medicaid Claims Volume",
      testId: "medicaid-claims-volume",
      tooltip: getClaimsVolumeDefinitions()
    },
    ...(featureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT) && {
      medicaidNewPatientInd: {
        alignment: TextAlignment.Left,
        displayText: "Medicaid New Patient Claims",
        testId: "medicaid-new-patient-claims",
        tooltip: newPatientClaimsToolTip
      },
    }),
    medicareClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Medicare Claims Volume",
      testId: "medicare-claims-volume",
      tooltip: getClaimsVolumeDefinitions()
    },
    ...(featureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT) && {
      medicareNewPatientInd: {
        alignment: TextAlignment.Left,
        displayText: "Medicare New Patient Claims",
        testId: "medicare-new-patient-claims",
        tooltip: newPatientClaimsToolTip
      },
    }),
    providerName: {
      alignment: TextAlignment.Left,
      displayText: "Name",
      testId: "name",
    },
    ...(!featureFlags.has(Features.PCI_DASHBOARD_TERMINATE_GHOST_PROVIDERS) && {
      specialtyGroupName: {
        alignment: TextAlignment.Left,
        displayText: "Specialty",
        testId: "specialty-group-name",
      },
    }),
    totalClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Total Claims Volume",
      testId: "total-claims-volume",
      tooltip: getClaimsVolumeDefinitions()
    },
    ...(featureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT) && {
      totalNewPatientInd: {
        alignment: TextAlignment.Left,
        displayText: "Total New Patient Claims",
        testId: "total-new-patient-claims",
        tooltip: newPatientClaimsToolTip
      },
    }),
    ...(projectContext.project.showVaClaimsVolumeColumn && {
      vaClaimsLabel: {
        alignment: TextAlignment.Left,
        displayText: "VA Claims Volume",
        testId: "va-claims-volume",
        tooltip: getClaimsVolumeDefinitions()
      },
      ...(featureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT) && {
        vaNewPatientInd: {
          alignment: TextAlignment.Left,
          displayText: "VA New Patient Claims",
          testId: "va-new-patient-claims",
          tooltip: newPatientClaimsToolTip
        }
      }),
    }),
    ...(projectContext.project.showTricareClaimsVolumeColumn && {
      tricareClaimsLabel: {
        alignment: TextAlignment.Left,
        displayText: "TRICARE Claims Volume",
        testId: "tricare-claims-volume",
        tooltip: getClaimsVolumeDefinitions()
      },
      ...(featureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT) && {
        tricareNewPatientInd: {
          alignment: TextAlignment.Left,
          displayText: "TRICARE New Patient Claims",
          testId: "tricare-new-patient-claims",
          tooltip: newPatientClaimsToolTip
        }
      }),
    }),
    ...(showTerminatedProviders && {
      terminationReason: {
        alignment: TextAlignment.Left,
        displayText: "Reason",
        minWidth: "85px",
        noSort: true,
        testId: "reason-label"
      }
    })
  };

  const customSort = (
    records: PciDashboardDetailsViewWaffleToppings[],
    column: keyof PciDashboardDetailsViewWaffleToppings,
    sortedDirection: SortDirections
  ): PciDashboardDetailsViewWaffleToppings[] => {
    let direction: SortDirection = null;

    switch (sortedDirection) {
      case SortDirections.Asc:
        direction = SortDirection.Ascending;
        break;
      case SortDirections.Desc:
        direction = SortDirection.Descending;
        break;
      case SortDirections.None:
        direction = SortDirection.None;
        break;
      default:
        direction = SortDirection.Unknown;
        break;
    }

    setSortDirection(direction);
    setSortedColumn(column);

    getDashboardDetails(showTerminatedProviders, true, column, direction);

    return records;
  }

  const getDashboardDetails = (
    showTerminated?: boolean,
    isSorting?: boolean,
    column: string = null,
    direction: SortDirection = null,
    isReloading?: boolean,
    loadingMore?: boolean,
  ) => {
    setLoading(true);

    const filtersPayload: PciDashboardFiltersPayload = cloneDeep(pciDashboardFiltersContext.displayedFilters);
    filtersPayload.sortedColumn = isSorting ? column : sortedColumn;
    filtersPayload.sortDirection = isSorting ? direction : sortDirection;
    filtersPayload.take = isSorting ? dashboardDetailsViewToppings.length : 100;
    filtersPayload.skip = (isSorting || !dashboardDetailsViewToppings.length) ? 0 : dashboardDetailsViewToppings.length;

    if (filtersPayload.claimsLabel == ClaimsActivityVolumeLabelEnum.Ghost && filtersPayload.providerNetworkType == ProviderNetworkType.Network && !filtersPayload.compareScenario && featureFlags.has(Features.PCI_DASHBOARD_TERMINATE_GHOST_PROVIDERS)) {
      filtersPayload.ignoreTake = true;
      filtersPayload.skip = dashboardDetailsViewToppings.length;
    }

    // if all specialties, counties, or county statuses are selected send an empty array to save resources
    if (filtersPayload.countySSAs.length === pciDashboardFiltersContext.counties.length) {
      filtersPayload.countySSAs = [];
    }

    if (filtersPayload.specialtyGroupCodes.length === pciDashboardFiltersContext.specialties.length) {
      filtersPayload.specialtyGroupCodes = [];
    }

    if (!filtersPayload.countyStatuses.includes(RegionSetStatusType.Opportunity) &&
      filtersPayload.countyStatuses.length === pciDashboardFiltersContext.countyStatuses.filter(x => x.key !== RegionSetStatusType.Opportunity).length) {
      filtersPayload.countyStatuses = [];
    }

    const buildData = (data: PciDashboardDetailsModel, isNetworkGhostFilters?: boolean, loadingMore?: boolean) => {
      if (!!data) {
        let waffleToppings: PciDashboardDetailsViewWaffleToppings[];
        if (featureFlags.has(Features.PCI_DASHBOARD_TERMINATE_GHOST_PROVIDERS) && isNetworkGhostFilters && (user.roles.includes(Roles.TERM_PROVIDER_VIEWER) || user.roles.includes(Roles.TERM_PROVIDER_ADMIN))) {
          setNpiAndTerminationId([])
          waffleToppings = data.summaries.map((summary, index) => {
            /* eslint-disable sort-keys */
            if (loadingMore) {
              const existingNpi = dashboardDetailsViewToppings.some((details: { key: string }) => {
                const npi = details.key.split('-')[0];
                if (npi == summary.npi) {
                  return npi
                }
              })
              if (existingNpi) {
                return null;
              }
            }
            if (restoringAll ? true : (!showTerminated ?
              (summary.terminationStatus === PciDashboardProviderTerminationStatusEnum.NotTerminated)
              :
              (summary.terminationStatus === PciDashboardProviderTerminationStatusEnum.Terminated || summary.terminationStatus === PciDashboardProviderTerminationStatusEnum.UnprocessedTermination))) {

              if (!summary.terminationId && !!summary.providerTermination?.providerTerminationProjectIds) {
                setNpiAndTerminationId((prevNpiandId: [string, number][]) => [...prevNpiandId, ...summary.providerTermination.providerTerminationProjectIds.map((termId: number) => [summary.npi, termId] as [string, number])]);
              }
              else {
                setNpiAndTerminationId((prevNpiandId: [string, number][]) => [...prevNpiandId, [summary.npi, summary.terminationId]]);
              }
              return {
                key: `${summary.npi}-${index}`,
                providerName: featureFlags.has(Features.PCI_DASHBOARD_TERMINATE_GHOST_PROVIDERS) ?
                  <>
                    <FlyoutLink
                      classNames="p-0 qes-button-secondary"
                      content={{
                        body: getProviderProfile(summary.providerId, summary.providerName, filtersPayload.providerNetworkType, summary.countySSA, summary.specialtyGroupCode, summary.specialtyGroupName, summary.npi) as JSX.Element,
                        title: "Provider Profile",
                      }}
                      testId={`pci-dashboard__provider-details-flyout--${summary.npi}`}
                    >
                      {summary.providerName ? summary.providerName : summary.npi}
                    </FlyoutLink>
                    <span className="text-muted ml15 d-block">NPI: {summary.npi}</span>
                  </> :
                  <React.Fragment>
                    <div>
                      {summary.providerName}
                    </div>
                    <div>
                      {summary.npi}
                    </div>
                  </React.Fragment>,
                specialtyGroupName: summary.specialtyGroupName,
                effectivenessRank: !!summary.effectivenessRank ? summary.effectivenessRank.toString() : "---",
                efficiencyRank: !!summary.efficiencyRank ? summary.efficiencyRank.toString() : "---",
                commercialClaimsLabel: !!summary.commercialClaimsLabel ? summary.commercialClaimsLabel : "---",
                commercialNewPatientInd: !!summary.commercialNewPatientInd ? summary.commercialNewPatientInd : "---",
                medicaidClaimsLabel: !!summary.medicaidClaimsLabel ? summary.medicaidClaimsLabel : "---",
                medicaidNewPatientInd: !!summary.medicaidNewPatientInd ? summary.medicaidNewPatientInd : "---",
                medicareClaimsLabel: !!summary.medicareClaimsLabel ? summary.medicareClaimsLabel : "---",
                medicareNewPatientInd: !!summary.medicareNewPatientInd ? summary.medicareNewPatientInd : "---",
                vaClaimsLabel: !!summary.vaClaimsLabel ? summary.vaClaimsLabel : "---",
                vaNewPatientInd: !!summary.vaNewPatientInd ? summary.vaNewPatientInd : "---",
                tricareClaimsLabel: !!summary.tricareClaimsLabel ? summary.tricareClaimsLabel : "---",
                tricareNewPatientInd: !!summary.tricareNewPatientInd ? summary.tricareNewPatientInd : "---",
                totalClaimsLabel: !!summary.totalClaimsLabel ? summary.totalClaimsLabel : "---",
                totalNewPatientInd: !!summary.totalNewPatientInd ? summary.totalNewPatientInd : "---",
                terminationReason: (() => {

                  let terminationString = "";
                  if (summary.providerTermination?.terminateByNPI) {
                    terminationString += "NPI";
                    if (summary.providerTermination?.terminateByTIN) {
                      terminationString += " and TIN";
                    }
                  } else if (summary.providerTermination?.terminateByTIN) {
                    terminationString += "TIN";
                  }

                  return (
                    <>
                      <div className="mb-3">
                        {summary.terminationStatus === PciDashboardProviderTerminationStatusEnum.UnprocessedTermination && <div className="font-weight-bold">UNPROCESSED</div>}
                        {user.roles.includes(Roles.TERM_PROVIDER_ADMIN) && (
                          <TooltipWrapper text={!!summary.providerTermination?.providerTerminationProjectId ? "Edit/View Terminated Provider Details" : ""}>
                            <FlyoutLink
                              disabled={!summary.providerTermination?.providerTerminationProjectId}
                              classNames="text-info font-weight-normal p-0"
                              content={{
                                body: updateOrViewTerminatedProviderPanel(summary.providerTermination, true),
                                title: "Termination Details",
                              }}
                              testId={`pci-dashboard__termination-details-flyout--edit--${summary.npi}`}
                            >
                              {summary.providerTermination?.reasonName ? summary.providerTermination?.reasonName : `Includes Multiple Terminations` }
                            </FlyoutLink>
                          </TooltipWrapper>
                        )}
                        {((!user.roles.includes(Roles.TERM_PROVIDER_ADMIN) && user.roles.includes(Roles.TERM_PROVIDER_VIEWER))) && (
                          <TooltipWrapper text={!!summary.providerTermination?.providerTerminationProjectId ? "View Terminated Provider Details" : ""}>
                            <FlyoutLink
                              classNames="text-info font-weight-normal p-0"
                              content={{
                                body: updateOrViewTerminatedProviderPanel(summary.providerTermination, false),
                                title: "Termination Details",
                              }}
                              testId={`pci-dashboard__termination-details-flyout--no-edit--${summary.npi}`}
                            >
                              {summary.providerTermination?.reasonName ? summary.providerTermination?.reasonName : `Includes Multiple Terminations` }
                            </FlyoutLink>
                          </TooltipWrapper>
                        )}
                      </div>
                      <div>
                        {terminationString != "" ?
                          `Terminated by ${terminationString}`
                          :
                          ""
                        }
                      </div>
                    </>
                  );
                })(),
              };
            }
            else {
              return null
            }
            /* eslint-enable sort-keys */
          }).filter((topping) => topping !== null);
        }
        else {
          waffleToppings = data.summaries.map((summary, index) => {
            /* eslint-disable sort-keys */
            return {
              key: `${summary.npi}-${index}`,
              providerName: featureFlags.has(Features.PCI_DASHBOARD_TERMINATE_GHOST_PROVIDERS) ? 
                <>
                  <FlyoutLink
                    classNames="p-0 qes-button-secondary"
                    content={{
                        body: getProviderProfile(summary.providerId, summary.providerName, filtersPayload.providerNetworkType, summary.countySSA, summary.specialtyGroupCode, summary.specialtyGroupName, summary.npi) as JSX.Element,
                        title: "Provider Profile",
                    }}
                    testId={`pci-dashboard__provider-details-flyout--${summary.npi}`}
                  >
                    {summary.providerName ? summary.providerName : summary.npi}
                  </FlyoutLink>
                  <span className="text-muted ml15 d-block">NPI: {summary.npi}</span>
                </> :
                <React.Fragment>
                  <div>
                    {summary.providerName}
                  </div>
                  <div>
                    {summary.npi}
                  </div>
                </React.Fragment>,
              specialtyGroupName: summary.specialtyGroupName,
              effectivenessRank: !!summary.effectivenessRank ? summary.effectivenessRank.toString() : "---",
              efficiencyRank: !!summary.efficiencyRank ? summary.efficiencyRank.toString() : "---",
              commercialClaimsLabel: !!summary.commercialClaimsLabel ? summary.commercialClaimsLabel : "---",
              commercialNewPatientInd: !!summary.commercialNewPatientInd ? summary.commercialNewPatientInd : "---",
              medicaidClaimsLabel: !!summary.medicaidClaimsLabel ? summary.medicaidClaimsLabel : "---",
              medicaidNewPatientInd: !!summary.medicaidNewPatientInd ? summary.medicaidNewPatientInd : "---",
              medicareClaimsLabel: !!summary.medicareClaimsLabel ? summary.medicareClaimsLabel : "---",
              medicareNewPatientInd: !!summary.medicareNewPatientInd ? summary.medicareNewPatientInd : "---",
              vaClaimsLabel: !!summary.vaClaimsLabel ? summary.vaClaimsLabel : "---",
              vaNewPatientInd: !!summary.vaNewPatientInd ? summary.vaNewPatientInd : "---",
              tricareClaimsLabel: !!summary.tricareClaimsLabel ? summary.tricareClaimsLabel : "---",
              tricareNewPatientInd: !!summary.tricareNewPatientInd ? summary.tricareNewPatientInd : "---",
              totalClaimsLabel: !!summary.totalClaimsLabel ? summary.totalClaimsLabel : "---",
              totalNewPatientInd: !!summary.totalNewPatientInd ? summary.totalNewPatientInd : "---",
            };
            /* eslint-enable sort-keys */
          });
        }

        if (restoringAll) {
          setRestoringAll(false);
        }

        if (filtersPayload.claimsLabel == ClaimsActivityVolumeLabelEnum.Ghost && filtersPayload.providerNetworkType == ProviderNetworkType.Network && !filtersPayload.compareScenario && featureFlags.has(Features.PCI_DASHBOARD_TERMINATE_GHOST_PROVIDERS)) {
          setTotalNotTerminatedCount(data.totalNotTerminatedCount);

          const loadMoreCount = dashboardDetailsViewToppings.length == 0 ? 100 : dashboardDetailsViewToppings.length
          waffleToppings = waffleToppings.slice(0, isReloading ? 100 : loadMoreCount)
        }

        if (isSorting || !dashboardDetailsViewToppings.length || isReloading) {
          setDashboardDetailsViewToppings(waffleToppings);
          setSelectedKeys([]);
        } else {
          let newToppings: PciDashboardDetailsViewWaffleToppings[] = cloneDeep(dashboardDetailsViewToppings);
          newToppings = newToppings.concat(waffleToppings);
          setDashboardDetailsViewToppings(newToppings);
        }

        setTotalCount(data.totalCount);
      }
      
      setLoading(false);
    }

    if (props.showLineOfBusiness) {
      if (!loadingMore) {
        filtersPayload.skip = 0;
      }
      pciDashboardService.getClaimsActivityDashboardDetails(projectContext.project.projectId, pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId, filtersPayload).then((data) => {

        if (filtersPayload.claimsLabel == ClaimsActivityVolumeLabelEnum.Ghost && filtersPayload.providerNetworkType == ProviderNetworkType.Network && !filtersPayload.compareScenario && featureFlags.has(Features.PCI_DASHBOARD_TERMINATE_GHOST_PROVIDERS) && (user.roles.includes(Roles.TERM_PROVIDER_ADMIN) || user.roles.includes(Roles.TERM_PROVIDER_VIEWER))) {
          pciDashboardService.getNetworkGhostProviderTerminationRecords(projectContext.project.projectId, pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId, filtersPayload).then((termRecords) => {

            if (!showTerminatedProviders) {
              setShowTerminateButton(true);
            }

            const notTermedNpis = termRecords
              .filter((record) => record.terminationStatus == PciDashboardProviderTerminationStatusEnum.NotTerminated)
              .map((notTermed) => notTermed.npi);

            setNpis(notTermedNpis);

            const updatedSummaries = data.summaries.map(summary => {
              const matchingRecord = termRecords.find(record => record.npi == summary.npi);

              if (matchingRecord) {
                if (matchingRecord.terminationStatus === PciDashboardProviderTerminationStatusEnum.Terminated || matchingRecord.terminationStatus === PciDashboardProviderTerminationStatusEnum.UnprocessedTermination) {
                  setTerminatedNpisExist(true);
                }
                if (matchingRecord.terminationStatus === PciDashboardProviderTerminationStatusEnum.Terminated || matchingRecord.terminationStatus === PciDashboardProviderTerminationStatusEnum.UnprocessedTermination) {
                  matchingRecord.terminateByNPI = matchingRecord.identifier == PciDashboardProviderTerminationTypeEnum.NPI ? true : false;
                  matchingRecord.terminateByTIN = matchingRecord.identifier == PciDashboardProviderTerminationTypeEnum.TIN ? true : false;
                  return {
                    ...summary,
                    providerTermination: matchingRecord,
                    terminationId: matchingRecord.providerTerminationProjectId,
                    terminationStatus: matchingRecord.terminationStatus
                  }
                }
                return { ...summary, terminationStatus: matchingRecord.terminationStatus }
              }
              return summary;
            });

            data.summaries = updatedSummaries;
            data.totalNotTerminatedCount = notTermedNpis.length;

            if (data.totalNotTerminatedCount == data.totalCount) {
              setShowTerminatedProviders(false);
              setTerminatedNpisExist(false);
            }
            setIsNetworkGhostFilters(true);
            buildData(data, true, loadingMore);
          }, (error) => {
            setError(error);
          });
        }
        else {
          setTotalNotTerminatedCount(totalCount);
          setShowTerminateButton(false);
          buildData(data);
        }
      }, (error) => {
        setError(error);
      });
    } else {
      pciDashboardService.getClaimsInsightsDashboardDetails(projectContext.project.projectId, pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId, filtersPayload).then((data) => {
        buildData(data);
      }, (error) => {
        setError(error);
      });
    }
  }

  const handleToggle = () => {
    setShowTerminatedProviders((prevState: boolean) => {
      const newState = !prevState;
      getDashboardDetails(newState, null, null, null, true);
      return newState;
    });
  }

  const handleToast = (errorStatus?: string) => {
    const toastText = !!errorStatus ?
      `${errorStatus} - There was an error submitting the report. Please try again, or contact your Client Services Representative.` :
      "Report was successfully submitted!"

    toast(toastText, {
      autoClose: !!errorStatus && false,
      containerId: "z-index-9999",
      type: !!errorStatus ? "error" : "success",
    });

    setCreateReportLoading(false);
  }

  const onCreateReportClick = () => {
    setCreateReportLoading(true);
    const filtersPayload: PciDashboardFiltersPayload = cloneDeep(pciDashboardFiltersContext.displayedFilters);

    exportService.postExportPciDashboardDetails(projectContext.project.projectId, projectContext.runConfiguration.projectRunConfigurationId, filtersPayload).then((response) => {
      if (!!response) {
        handleToast();
      }
    }).catch((error) => {
      handleToast(error?.status);
    });
  }

  const createTerminateProvidersPanel = () => {
    return (
      <TerminateProvidersPanel
        onSubmit={() => {
          projectContext.setProjectHasUnprocessedTerminations(true);
          setDashboardDetailsViewToppings([]);
        }}
        projectId={projectContext.project.projectId}
        npis={selectedKeys.length == 0 ? npis : selectedKeys.map((key: string) => key.split('-')[0])}
        terminatePanelMode={TerminatePanelMode.Create}
      />
    );
  }

  const updateOrViewTerminatedProviderPanel = (terminatedProvider: PciDashboardTerminationDetails, canEdit: boolean) => {
    
    return (
      <TerminateProvidersPanel
        projectId={projectContext.project.projectId}
        terminatePanelMode={canEdit ? TerminatePanelMode.Edit : TerminatePanelMode.View}
        termination={terminatedProvider}
        onSubmit={() => {
          getDashboardDetails(true, null, null, null, true);
        }}
      />
    )
  }

  const onBackToFullViewClick = () => {
    const filters: PciDashboardFiltersPayload = pciDashboardFiltersContext.getFiltersPayload();
    pciDashboardFiltersContext.setDisplayedFilters(filters);
    props.setShowDashboardDetails(false);
  }

  const onCheckboxChange = (newSelectedKeys: string[]) => {
    setSelectedKeys(newSelectedKeys);
  }

  function getProviderProfile(providerId: number, providerName: string, providerNetworkType: ProviderNetworkType, countySSA: string, specialtyGroupCode: string, specialtyGroupName : string, npi: string): JSX.Element {
    return (
      <PciDashboardProviderFlyout
        providerId={providerId}
        providerName={providerName}
        providerNetworkType={providerNetworkType}
        project={projectContext.project}
        runConfiguration={projectContext.runConfiguration}
        countySSA={countySSA}
        specialtyGroupCode={specialtyGroupCode}
        specialtyGroupName={specialtyGroupName}
        npi={npi} />
    );
  }

  useEffect(() => {
    if (!!error) {
      throw error;
    }
  }, [error]);

  return (
    <React.Fragment>
      <PageHeader disableHR={true} title="Dashboard Details">
        <PageHeaderRight>
          <QESButton
            onClick={() => onBackToFullViewClick()}
            qesTestId="pci-dashboard__dashboard-details--back-to-full-view"
            qesType="primary"
          >
            Back to Full View
          </QESButton>
        </PageHeaderRight>
      </PageHeader>
      <PciDashboardFiltersOverview
        isDashboardDetailsView={true}
        showLineOfBusiness={props.showLineOfBusiness}
        showNewPatientClaims={featureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT)}
      />
      <div className="row">
        {terminatedNpisExist && (
          <div className="col-12 d-flex justify-content-end">
            <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN, Roles.TERM_PROVIDER_VIEWER]}>
              <IconToggle
                active={showTerminatedProviders}
                onToggle={() => handleToggle()}
                activeIconClass={["fa", "fa-toggle-on"]}
                inActiveIconClass={["fa", "fa-toggle-off"]}
                baseClassName={["icon-toggle", "mb-0", "pb-0", "pt-0"]}
              >
                Terminated
                </IconToggle>
            </RoleRestricted>
          </div>
        )}
        <div className="col-12 pci-dashboard__details-hide-all-checkbox">
          <Waffles<PciDashboardDetailsViewWaffleToppings>
            bulkActionBar={(
              <div>
                <QESButton
                  className="px-0"
                  disabled={createReportLoading}
                  onClick={() => onCreateReportClick()}
                  qesTestId="pci-dashboard__dashboard-details--create-report"
                  qesType="secondary"
                >
                  Create Report
                </QESButton>

                {showTerminateButton && !showTerminatedProviders && featureFlags.has(Features.PCI_DASHBOARD_TERMINATE_GHOST_PROVIDERS) && (
                  <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
                    {!dashboardDetailsViewToppings.length ? (
                        <FlyoutLink
                          classNames="p-3"
                          content={{
                            body: createTerminateProvidersPanel(),
                            title: `Terminate Ghost Providers`,
                          }}
                          disabled={true}
                          testId="pci-dasboard__dashboard-details--terminate-flyout-disabled"
                        >
                          <QesIcon iconName={QesIcons.FontAwesome.UserTimes} isDecorative={true} /> Terminate
                        </FlyoutLink>
                    ): (
                        <TooltipWrapper text={selectedKeys.length ? (selectedKeys.length == 1 ? `Terminate selected provider` : `Terminate selected providers`) : `Terminate all network ghost providers`} containerClassName="ml-3">
                          <FlyoutLink
                            classNames="p-0"
                            content={{
                              body: createTerminateProvidersPanel(),
                              title: `Terminate Ghost Providers`,
                            }}
                            disabled={false}
                            testId="pci-dasboard__dashboard-details--terminate-flyout"
                          >
                            <QesIcon iconName={QesIcons.FontAwesome.UserTimes} isDecorative={true} /> {selectedKeys.length ? "Terminate" : "Terminate All Providers"}
                          </FlyoutLink>
                        </TooltipWrapper>
                    )}
                  </RoleRestricted>
                )}

                {showTerminatedProviders && featureFlags.has(Features.PCI_DASHBOARD_TERMINATE_GHOST_PROVIDERS) && (
                  <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
                    <TooltipWrapper text={`${!selectedKeys.length ? "Restore all network ghost providers" : (selectedKeys.length > 1 || !selectedKeys.length) ? "Restore selected providers" : "Restore selected provider"}`} containerClassName="ml-3">
                      <ModalLink
                        content={{
                          body:
                            <div>
                              <QESAlert classNames="mb-3" type="warning" persist={true}>
                                {`This will restore all terminations associated with the selected ${!selectedKeys.length || selectedKeys.length > 1 ? "NPIs" : "NPI"}.`}
                              </QESAlert>
                              <div>
                                {`${!selectedKeys.length || selectedKeys.length > 1 ? "Are you sure you want to restore the selected providers?" : "Are you sure you want to restore the selected provider?"}`}
                              </div>
                            </div>,
                          cancelButtonText: "Cancel",
                          confirmButtonAction: async () => {
                            const selectedNpis = selectedKeys.map((key: string) => key.split('-')[0])

                            const terminateIds = !selectedNpis.length ?
                              npiAndTerminationId
                                .map((npiAndTermId: [string, number]) => npiAndTermId[1])
                              :
                              npiAndTerminationId
                                .filter(([npi]: [string, number]) => selectedNpis.includes(npi))
                                .map(([, id]: [string, number]) => id);

                            providerTerminationService.deleteTermination(
                              projectContext.project.projectId,
                              terminateIds
                            )

                            setRestoringAll(!selectedKeys.length);

                            if (selectedKeys.length === npiAndTerminationId.length) {
                              setShowTerminatedProviders(false);
                            }
                            projectContext.setProjectHasUnprocessedTerminations(true);
                          },
                          confirmButtonText: "Restore",
                          title: `${selectedKeys.length > 1 ? "Restore Providers" : "Restore Provider"}`,
                        }}
                        testId="pci-dashboard__restore-modal"
                        classNames="p-0"
                      >
                        <QesIcon iconName={QesIcons.FontAwesome.User} isDecorative={true} /> {!selectedKeys.length ? "Restore All Providers" : "Restore"}
                      </ModalLink>
                    </TooltipWrapper>
                  </RoleRestricted>
                )}
              </div>
            )}
            columns={columnConfig}
            customSort={customSort}
            data={dashboardDetailsViewToppings}
            handleCheckboxChange={onCheckboxChange}
            fullWidth={true}
            loading={loading}
            passThroughSorting={true}
            selectedKeys={
              dashboardDetailsViewToppings
                .filter((provider: PciDashboardDetailsViewWaffleToppings) =>
                  selectedKeys.includes(provider.key)
                )
                .map((provider: PciDashboardDetailsViewWaffleToppings) => provider.key)
            }
            showCheckboxes={showTerminateButton && featureFlags.has(Features.PCI_DASHBOARD_TERMINATE_GHOST_PROVIDERS) && user.roles.includes(Roles.TERM_PROVIDER_ADMIN)}
            testId="pci-dashboard__dashboard-details-table"
            totalNumberOfRecords={dashboardDetailsViewToppings.length}
          />

          {(
            (isNetworkGhostFilters && featureFlags.has(Features.PCI_DASHBOARD_TERMINATE_GHOST_PROVIDERS)
              ? (showTerminatedProviders
                ? (totalCount - (totalNotTerminatedCount ?? 0)) > dashboardDetailsViewToppings.length
                : (totalNotTerminatedCount ?? totalCount) > dashboardDetailsViewToppings.length)
              : totalCount > dashboardDetailsViewToppings.length
            ) && (
              <div className="row">
                <div className="col-12">
                  <div className="text-center">
                    <QESButton
                      className="mt-3"
                      disabled={loading}
                      onClick={() => getDashboardDetails(showTerminatedProviders, null, null, null, null, true)}
                      qesTestId="pci-dashboard__dashboard-details--load-more"
                      qesType="primary"
                    >
                      Load More
                    </QESButton>
                  </div>
                </div>
              </div>
            )
          )}

        </div>
      </div>
    </React.Fragment>
  )
}

export default DashboardDetailsView;
